import React from 'react';
import type { ImagePickerResponse } from 'react-native-image-picker';
import { err, ok } from 'neverthrow';

import { DropzoneFile } from '@rover/types/src/ImagePicker';

import { toJpeg } from '../../utils/imageMagickUtils';
import { Logger } from '../Logging';

import { fileContentsDoNotMatchExtension } from './fileVerification';
import {
  filesToMediaFiles,
  getSelectMediaDropzoneProps,
  getSelectMediaInputProps,
} from './internals.common';
import MediaFile from './MediaFile';
import {
  MediaSelectionCorruptedFileError,
  MediaSelectionInvalidFileTypeError,
  MediaSelectionNoFilesError,
  MediaSelectionUnknownError,
} from './MediaSelectionError';
import {
  ImagePickerResizeResult,
  MediaType,
  SelectMediaOptions,
  SelectMediaResult,
  ShowActionSheetWithOptions,
} from './types';

const filesToJpegFiles = async (files: MediaFile[]): Promise<MediaFile[]> => {
  const jpegFiles = await Promise.all(
    files.map(async (file) => {
      const jpegFile = await toJpeg(file);
      return new MediaFile(jpegFile);
    })
  );
  return jpegFiles;
};

export const getFilesFromSelectionEvent = async (
  event: React.ChangeEvent<HTMLInputElement>
): Promise<SelectMediaResult> => {
  Logger.debug('rsdk.media.selectMedia.start');
  // Metrics.increment('rsdk.media.selectMedia.start');

  try {
    const { files } = event?.target || {};
    // ensure files exist
    if (!files?.length) {
      const error = new MediaSelectionNoFilesError();

      Logger.error(error);
      // Metrics.increment('rsdk.media.selectMedia.error', { tags: { errorType: 'no-files' }});

      // In the case of a user selecting a file, then clicking the file input again,
      // then cancelling the dialog: Chrome will empty the input and fire a change event,
      // but Safari and Firefox will NOT empty the input or fire the change event.
      // This error is returned in the Chrome case.
      return err(error);
    }

    if (await fileContentsDoNotMatchExtension(Array.from(files))) {
      return err(new MediaSelectionCorruptedFileError());
    }

    let imageMediaFiles: MediaFile[] = [];
    const videoMediaFiles: MediaFile[] = [];
    try {
      const mediaFiles = await filesToMediaFiles(files);

      mediaFiles.forEach((file) => {
        if (file.type === MediaType.IMAGE) {
          imageMediaFiles.push(file);
        } else videoMediaFiles.push(file);
      });

      // Convert all images to JPEG
      imageMediaFiles = await filesToJpegFiles(imageMediaFiles);
    } catch {
      const error = new MediaSelectionInvalidFileTypeError();

      Logger.error(error);
      // Metrics.increment('rsdk.media.selectMedia.error', { tags: { errorType: 'invalid-file-type' }});

      return err(error);
    }

    Logger.debug('rsdk.media.selectMedia.success');
    // mediaFiles.forEach((file) => {
    //   Metrics.increment('rsdk.media.selectMedia.success', {
    //     // see https://roverdotcom.slack.com/archives/CB5TF01FB/p1646262297400549 for discussion on these tags
    //     tags: { fileType: file.type, fileSizeBucket: getFileSizeBucket(file) },
    //   });
    // });

    return ok([...imageMediaFiles, ...videoMediaFiles]);
  } catch (error) {
    Logger.error(Error(`Unknown error: ${error}`));
    // Metrics.increment('rsdk.media.selectMedia.error', { tags: { errorType: 'unknown' }});

    // Q: Do we need to do more than metrics and logging in the case of an unknown error?
    //    Like captureError() or whatever RSDK version of that

    return err(new MediaSelectionUnknownError(error));
  }
};

// TODO: Instrument with Logger and Metrics calls like getFilesFromSelectionEvent above
export async function getFilesFromDropzoneDrop(
  acceptedFiles: DropzoneFile[],
  rejectedFiles: DropzoneFile[]
): Promise<SelectMediaResult> {
  // handle wrong file type case
  if (rejectedFiles.length && !acceptedFiles.length) {
    return err(new MediaSelectionInvalidFileTypeError());
  }
  if (acceptedFiles.length) {
    if (await fileContentsDoNotMatchExtension(acceptedFiles)) {
      return err(new MediaSelectionCorruptedFileError());
    }
    try {
      const mediaFiles = acceptedFiles.map((file) => new MediaFile(file));
      return ok(mediaFiles);
    } catch (error) {
      return err(new MediaSelectionUnknownError(error));
    }
  }
  return err(new MediaSelectionNoFilesError());
}

/* eslint-disable @typescript-eslint/no-unused-vars */
export function getFilesFromImagePickerResponse(response: ImagePickerResponse): SelectMediaResult {
  return err(new MediaSelectionUnknownError('Not implemented for web'));
}

export async function mediaSelectionActionSheet(
  showActionSheetWithOptions: ShowActionSheetWithOptions,
  options: SelectMediaOptions
): Promise<ImagePickerResizeResult> {
  return err(new MediaSelectionUnknownError('Not implemented for web'));
}
/* eslint-enable @typescript-eslint/no-unused-vars */

export { filesToMediaFiles, getSelectMediaInputProps, getSelectMediaDropzoneProps };
